// Photo blocks → <figure>. Sites wrap each photo in layers of text-less divs and
// links (Pinkbike: div.media-full-width > a > div > div > img, then a caption div);
// Readability takes those for clutter and drops them. Turning each block into a
// plain <figure><img><figcaption> first keeps the photos and their captions.
// Runs on the server (linkedom) and on the phone (the WebView's own DOM), so it
// uses only the DOM methods both have. Kept free of other imports: the phone gets
// this file on its own (packages/tokens/generate.mjs).

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Before/after image comparisons (The Verge's image-comparison block; "before-after",
 * Juxtapose, TwentyTwenty elsewhere): two images and maybe a caption in a block with no
 * text, which Readability drops whole. Each becomes <figure data-compare> with both
 * images and the caption; the readers make it a slider (compare.ts).
 */
export function compareBlocksToFigures(doc: any): number {
  let made = 0;
  const named = /(?:^|[\s_-])(?:(?:image|img|photo)[-_]?compar(?:e|ison)|before[-_]?after|juxtapose|twentytwenty)(?:[\s_-]|$)/i;
  for (const el of Array.from(doc.querySelectorAll("[class], [id]")) as any[]) {
    if (!el.isConnected || el.closest("figure[data-compare]")) continue;
    if (!named.test(`${el.getAttribute("class") || ""} ${el.getAttribute("id") || ""}`)) continue;
    const imgs = (Array.from(el.querySelectorAll("img")) as any[]).filter((i) => {
      const s = i.getAttribute("src") || i.getAttribute("data-src") || "";
      return s && !s.startsWith("data:");
    });
    if (imgs.length !== 2) continue;
    const cap = el.querySelector("figcaption, [class*='caption' i]");
    const fig = doc.createElement("figure");
    fig.setAttribute("data-compare", "1"); // a value: an empty attribute is lost when linkedom clones the page
    for (const i of imgs) {
      const img = doc.createElement("img");
      img.setAttribute("src", i.getAttribute("src") || i.getAttribute("data-src"));
      for (const a of ["srcset", "sizes", "alt", "width", "height"]) { const v = i.getAttribute(a); if (v) img.setAttribute(a, v); }
      fig.appendChild(img);
    }
    const text = cap ? (cap.textContent || "").replace(/\s+/g, " ").trim() : "";
    if (text) { const fc = doc.createElement("figcaption"); fc.textContent = text; fig.appendChild(fc); }
    el.replaceWith(fig);
    made++;
  }
  return made;
}

export function photoBlocksToFigures(doc: any): number {
  let made = 0;
  compareBlocksToFigures(doc);
  // lazy-loading pages keep the real address in data-* attributes
  for (const img of Array.from(doc.querySelectorAll("img")) as any[]) {
    const lazy = img.getAttribute("data-src") || img.getAttribute("data-lazy-src") || img.getAttribute("data-original");
    if (lazy && (!img.getAttribute("src") || img.getAttribute("src").startsWith("data:"))) img.setAttribute("src", lazy);
  }
  for (const el of Array.from(doc.querySelectorAll("div, span, a")) as any[]) {
    if (!el.isConnected || el.closest("figure")) continue;
    const imgs = el.querySelectorAll("img");
    if (imgs.length !== 1) continue;
    const img = imgs[0];
    const src = img.getAttribute("src");
    if (!src || src.startsWith("data:")) continue;
    // the block holds nothing but the photo and, at most, its caption
    const cap = el.querySelector('[class*="caption" i], [class*="credit" i]');
    const text = (el.textContent || "").replace(/\s+/g, " ").trim();
    const capText = cap ? (cap.textContent || "").replace(/\s+/g, " ").trim() : "";
    if (text !== capText || el.querySelector("video, iframe, picture source")) continue;
    // use the outermost such block, so the caption beside the photo comes along
    const parent = el.parentElement;
    if (parent && parent.tagName !== "BODY" && parent.querySelectorAll("img").length === 1 &&
        (parent.textContent || "").replace(/\s+/g, " ").trim() === text) continue;
    const fig = doc.createElement("figure");
    const pic = doc.createElement("img");
    pic.setAttribute("src", src);
    for (const a of ["srcset", "sizes", "alt", "width", "height"]) { const v = img.getAttribute(a); if (v) pic.setAttribute(a, v); }
    fig.appendChild(pic);
    if (capText) { const fc = doc.createElement("figcaption"); fc.textContent = capText; fig.appendChild(fc); }
    el.replaceWith(fig);
    made++;
  }
  // Rows of photos (ENDURO's side-by-side pairs, gallery rows): a block holding only figures
  // reads to Readability as clutter (many images, no text) and goes. Unwrap it, so its
  // figures stand in the article one after another; data-row (2 or 3) lets a wide screen
  // set them side by side again, as the publisher does.
  for (const row of Array.from(doc.querySelectorAll("div, section, p")) as any[]) {
    // a figure alone in its own column wrapper (Pinkbike's two-column photo pairs) counts as one
    const flat = (s: string) => (s || "").replace(/\s+/g, "");
    const own = (k: any) => {
      if (k.tagName === "FIGURE") return k;
      const f = k.querySelectorAll("figure").length === 1 ? k.querySelector("figure") : null;
      return f && flat(k.textContent) === flat(f.textContent) && !k.querySelector("iframe, video") ? f : null;
    };
    const kids = (Array.from(row.children) as any[]).map(own);
    if (kids.length < 2 || !kids.every(Boolean)) continue;
    if (Array.from(row.childNodes as any[]).some((n: any) => n.nodeType === 3 && n.textContent.trim())) continue;
    // one photo twice (a page's small- and large-screen copies, The Verge) is one photo, not a pair
    const seen = new Set<string>();
    const photos = kids.filter((k) => {
      const key = photoKey(k.querySelector("img")?.getAttribute("src") || "");
      if (key && seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    for (const k of photos) {
      if (photos.length > 1 && photos.length <= 3) k.setAttribute("data-row", String(photos.length));
      row.parentNode.insertBefore(k, row);
    }
    row.remove();
  }
  return made;
}

/**
 * Players and charts that may play in an article: YouTube, Vimeo, Instagram, and
 * Flourish and Datawrapper charts (Pinkbike's race analyses are built from them).
 * Unanchored, as Readability tests it against any attribute.
 */
// the players an article may carry: video (YouTube, Vimeo), Instagram posts, charts (Flourish,
// Datawrapper) and podcast episodes (Buzzsprout, Spotify, Apple Podcasts, Simplecast, Transistor, Captivate)
export const EMBED_PLAYERS = /\/\/(?:www\.)?(?:youtube(?:-nocookie)?\.com\/embed\/|player\.vimeo\.com\/video\/|instagram\.com\/(?:p|reel)\/[\w-]+\/embed|flo\.uri\.sh\/(?:visualisation|story)\/\d+\/embed|datawrapper\.dwcdn\.net\/\w+|buzzsprout\.com\/\d+\/\d+[\w-]*\?[^"]*iframe=true|open\.spotify\.com\/embed\/(?:episode|show)\/|embed\.podcasts\.apple\.com\/|player\.simplecast\.com\/|share\.transistor\.fm\/e\/|player\.captivate\.fm\/)/i;

/**
 * Player blocks → <figure>. A video or chart often sits in its own section with no
 * text (Pinkbike: div.media-media-width > div > div > div > iframe), which Readability
 * leaves out of the article. Each becomes a plain <figure><iframe> where it stood.
 */
export function embedBlocksToFigures(doc: any): number {
  let made = 0;
  scriptPlayers(doc);
  for (const f of Array.from(doc.querySelectorAll("iframe")) as any[]) {
    let src = f.getAttribute("src") || f.getAttribute("data-src") || f.getAttribute("data-lazy-src") || "";
    if (src.startsWith("//")) src = "https:" + src;
    if (!EMBED_PLAYERS.test(src) || f.closest("figure")) continue;
    // the outermost wrapper that holds nothing else (no text, no other media), but never the article
    // itself: a video post whose bio has gone is an article holding one player, and replacing the
    // article would lose its mark (itemprop="articleBody") and leave Readability the footer (NSMB)
    const articleRoot = (el: any) => el.tagName === "BODY" || el.tagName === "ARTICLE" || el.tagName === "MAIN" || el.getAttribute?.("itemprop") === "articleBody";
    let block = f;
    while (block.parentElement && !articleRoot(block.parentElement) &&
      !(block.parentElement.textContent || "").trim() && block.parentElement.querySelectorAll("iframe, img, video").length === 1) block = block.parentElement;
    const fig = doc.createElement("figure");
    const frame = doc.createElement("iframe");
    frame.setAttribute("src", src.replace(/^http:/, "https:"));
    frame.setAttribute("allowfullscreen", "");
    const title = f.getAttribute("title");
    if (title) frame.setAttribute("title", title);
    fig.appendChild(frame);
    block.replaceWith(fig);
    made++;
  }
  return made;
}

/**
 * A podcast player a page draws with a script (Buzzsprout: an empty box and a script that fills
 * it, NSMB) becomes the same player as a frame, which the readers can show; scripts never run there.
 */
function scriptPlayers(doc: any): void {
  for (const script of Array.from(doc.querySelectorAll('script[src*="buzzsprout.com/"]')) as any[]) {
    const m = /buzzsprout\.com\/(\d+)\/episodes\/(\d+[\w-]*)\.js/.exec(script.getAttribute("src") || "");
    if (!m) continue;
    const box = doc.getElementById(`buzzsprout-player-${m[2]!.split("-")[0]}`);
    const frame = doc.createElement("iframe");
    frame.setAttribute("src", `https://www.buzzsprout.com/${m[1]}/${m[2]}?client_source=small_player&iframe=true`);
    frame.setAttribute("title", "Podcast episode");
    (box || script).replaceWith(frame);
    if (box) script.remove();
  }
}

/**
 * Players Readability left behind. It builds the article from the blocks around the
 * text, so a video in a block of its own beside them (NSMB puts each paragraph and each
 * video in separate blocks) can be dropped. Every player that was in [source] (the page
 * as prepared, before Readability) and is missing from [content] goes back in, before
 * the text that followed it on the page, or after the text before it.
 */
export function restorePlayers(source: any, content: string, doc: any): string {
  // players, image comparisons and photos, each known by its address (a comparison or a photo
  // by its first image)
  const keyOf = (fig: any): string => {
    const f = fig.querySelector(":scope > iframe");
    const src = f ? f.getAttribute("src") : (fig.querySelector("img") || { getAttribute: () => "" }).getAttribute("src");
    return (src || "").split("?")[0];
  };
  const isPlayer = (fig: any) => { const f = fig.querySelector(":scope > iframe"); return !!f && EMBED_PLAYERS.test(f.getAttribute("src") || ""); };
  const isPhoto = (fig: any) => !fig.hasAttribute("data-compare") && !fig.querySelector("iframe") && !!fig.querySelector("img");
  const figures = (Array.from(source.querySelectorAll("figure")) as any[]).filter((fig) => fig.hasAttribute("data-compare") || isPlayer(fig) || isPhoto(fig));
  if (!figures.length) return content;
  const norm = (s: string) => (s || "").replace(/\s+/g, " ").trim().slice(0, 200);
  const out = doc.createElement("div");
  out.innerHTML = content;
  const have = new Set([
    ...(Array.from(out.querySelectorAll("figure")) as any[]).map(keyOf),
    ...(Array.from(out.querySelectorAll("img")) as any[]).map((i) => (i.getAttribute("src") || "").split("?")[0]),
  ].filter(Boolean));
  const blocks = (Array.from(out.querySelectorAll("p, h2, h3, h4, li, blockquote")) as any[]).filter((b) => !b.closest("figure"));
  const find = (text: string) => text ? blocks.find((b) => norm(b.textContent).startsWith(text)) : undefined;
  // the nearest block before or after a figure, in page order, that made it into the article
  // (captions are not text; a menu or list between the figure and the prose is skipped)
  // page order by position in a full element list (linkedom's compareDocumentPosition misorders
  // elements at different depths)
  const order = new Map<any, number>();
  const walk = (el: any) => { order.set(el, order.size); for (const c of Array.from(el.children || []) as any[]) walk(c); };
  walk(source);
  const all = (Array.from(source.querySelectorAll("p, h2, h3, h4, li, blockquote")) as any[]).filter((b) => !b.closest("figure"));
  const near = (fig: any, dir: "next" | "prev"): any => {
    const at = order.get(fig) ?? 0;
    const list = dir === "next" ? all : all.slice().reverse();
    for (const b of list) {
      const o = order.get(b) ?? 0;
      if (dir === "next" ? o <= at : o >= at) continue;
      const hit = find(norm(b.textContent));
      if (hit) return hit;
    }
    return undefined;
  };
  // a figure Readability left empty (only its caption) gives way to the restored one
  for (const left of Array.from(out.querySelectorAll("figure")) as any[]) if (!left.querySelector("img, iframe, video")) left.remove();
  let changed = false;
  for (const fig of figures) {
    const key = keyOf(fig);
    if (!key || have.has(key)) continue;
    const copy = fig.cloneNode(true);
    const after = near(fig, "next");
    const before = after && !isPhoto(fig) ? undefined : near(fig, "prev");
    // a photo comes back only between two paragraphs of the article (never a sidebar's or a header's)
    if (isPhoto(fig) && !(after && before)) continue;
    if (after) after.parentElement.insertBefore(copy, after);
    else if (before) before.parentElement.insertBefore(copy, before.nextSibling);
    else continue;
    have.add(key);
    changed = true;
  }
  return changed ? out.innerHTML : content;
}

/**
 * A page that marks its article body (schema.org's itemprop="articleBody", as NSMB
 * does) has said where the article is: read only that. Short posts (a line and a
 * video) otherwise lose to a longer block nearby, such as the author's bio.
 */
/**
 * Subheads set as a bold line of their own between line breaks (Pinkbike writes "Size and
 * weight", "Durability" as <span class="bold"> followed by <br>s) become real headings; the
 * look is otherwise lost with the class, and the article reads as one long run of text.
 */
export function boldLinesToHeadings(doc: any): number {
  let made = 0;
  const bold = Array.from(doc.querySelectorAll("b, strong, span[class~='bold']")) as any[];
  const sib = (n: any, dir: "prev" | "next") => { let s = dir === "prev" ? n.previousSibling : n.nextSibling; while (s && s.nodeType === 3 && !s.textContent.trim()) s = dir === "prev" ? s.previousSibling : s.nextSibling; return s; };
  for (const el of bold) {
    if (!el.isConnected || el.closest("h1, h2, h3, h4, h5, h6, li, a, button, figure, table, p")) continue;
    const t = (el.textContent || "").replace(/\s+/g, " ").trim();
    if (t.length < 2 || t.length > 70 || /[:.,;]$/.test(t)) continue;
    const before = sib(el, "prev"), after = sib(el, "next");
    if (before && before.nodeName !== "BR") continue;
    if (after && after.nodeName !== "BR") continue;
    const h = doc.createElement("h3");
    h.textContent = t;
    // the line breaks that made the space around it are the heading's own margins now
    for (const s of [after, after && sib(after, "next")]) if (s && s.nodeName === "BR") s.remove();
    el.replaceWith(h);
    made++;
  }
  return made;
}

/**
 * Text written as runs separated by blank lines (<br><br>) rather than paragraphs (Pinkbike)
 * becomes paragraphs, as Readability would make it, but before the steps that place photos
 * and players between paragraphs, so they can find their neighbours. A single <br> stays a
 * line break within its paragraph (a schedule, a list of conditions).
 */
export function textRunsToParagraphs(doc: any): number {
  let made = 0;
  const blockTag = /^(?:DIV|P|H[1-6]|UL|OL|FIGURE|TABLE|BLOCKQUOTE|HR|IFRAME|VIDEO|SECTION|PRE)$/;
  for (const el of Array.from(doc.querySelectorAll("div, section, td, article")) as any[]) {
    if (!el.isConnected || el.closest("figure, li, table")) continue;
    const nodes = Array.from(el.childNodes) as any[];
    if (!nodes.some((n) => n.nodeName === "BR") || !nodes.some((n) => n.nodeType === 3 && n.textContent.trim().length > 40)) continue;
    let run: any[] = [];
    const flush = () => {
      while (run.length && (run[run.length - 1].nodeName === "BR" || (run[run.length - 1].nodeType === 3 && !run[run.length - 1].textContent.trim()))) run.pop();
      if (run.some((n) => (n.textContent || "").trim())) {
        if (run[0].nodeType === 3) run[0].textContent = run[0].textContent.trimStart();
        const p = doc.createElement("p");
        el.insertBefore(p, run[0]);
        for (const n of run) p.appendChild(n);
        made++;
      }
      run = [];
    };
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      if (n.nodeType === 1 && blockTag.test(n.nodeName)) { flush(); continue; }
      if (n.nodeName === "BR") {
        // two or more line breaks in a row (whitespace between them aside) end the paragraph
        let j = i + 1, brs = 1;
        while (j < nodes.length && (nodes[j].nodeName === "BR" || (nodes[j].nodeType === 3 && !nodes[j].textContent.trim()))) { if (nodes[j].nodeName === "BR") brs++; j++; }
        if (brs >= 2) { flush(); for (let k = i; k < j; k++) if (nodes[k].nodeName === "BR") nodes[k].remove(); i = j - 1; continue; }
      }
      // a line break right after a block (a photo, a rule) only made the gap the paragraph has anyway
      if (!run.length && n.nodeName === "BR") { n.remove(); continue; }
      if (!run.length && n.nodeType === 3 && !n.textContent.trim()) continue;
      run.push(n);
    }
    flush();
  }
  return made;
}

/**
 * The version of this page reading. A stored copy remembers the version that read it; opening
 * a copy read by an older one reads the page again (server, or the phone for sites that refuse
 * servers). Bump it with each improvement:
 *   2 photos in wrapper blocks · 3 comments never the article · 4 players, charts, photo rows,
 *   related lists, bios, footers · 5 (phone) the same, again · 6 photos between paragraphs,
 *   bold subheads, the lead photo's caption · 7 text runs as paragraphs, photo pairs in columns ·
 *   8 the version each copy is read with is kept
 */
export const EXTRACTOR = 15; // Verge Daily signup remnants no longer hide the closing-pitch cleanup

/** An image address without its size suffix or query, to recognise one photo at two sizes. */
const photoKey = (u: string) => (u || "").split(/[?#]/)[0]!.split("/").pop()!.replace(/\.(?:width|fill|height)-[\w-]+(?=\.)/i, "").replace(/-\d+x\d+(?=\.)/, "").toLowerCase();

const squash = (s: string) => (s || "").replace(/\s+/g, " ").trim();
const metaOf = (doc: any, sel: string) => (doc.querySelector(sel) || { getAttribute: () => "" }).getAttribute("content") || "";

/**
 * What the publisher puts above the article, found before anything is removed: the subtitle
 * under the headline (when the page also gives it as its description, so it is the
 * publisher's own summary and not the first paragraph), then the lead photo with its caption
 * ("A Ukrainian soldier of the 61st…", Ars). Pages often keep both in a header that Readability
 * leaves out (The Verge). withLead puts them at the top of the article, unless it holds them already.
 */
export function leadFigure(doc: any): string {
  return leadDek(doc) + leadPhoto(doc);
}

/** The subtitle under the headline, as a paragraph marked data-dek (the readers set it apart). */
function leadDek(doc: any): string {
  const descs = [metaOf(doc, 'meta[property="og:description"]'), metaOf(doc, 'meta[name="description"]'), metaOf(doc, 'meta[name="twitter:description"]')].map(squash).filter(Boolean);
  const h1 = doc.querySelector("h1");
  if (!descs.length || !h1) return "";
  // the next few elements after the headline, in its own header
  let el = h1.nextElementSibling;
  for (let i = 0; el && i < 3; i++, el = el.nextElementSibling) {
    const text = squash(el.textContent || "");
    if (text.length < 20 || text.length > 300 || el.querySelector("img, a")) continue;
    if (!descs.includes(text)) continue;
    const p = doc.createElement("p");
    p.setAttribute("data-dek", "");
    p.textContent = text;
    return p.outerHTML;
  }
  return "";
}

/** The page's lead photo (its og:image, shown on the page), with its caption when it has one. */
function leadPhoto(doc: any): string {
  const og = metaOf(doc, 'meta[property="og:image"]');
  if (!og) return "";
  const key = photoKey(og);
  for (const img of Array.from(doc.querySelectorAll("img")) as any[]) {
    const urls = [img.getAttribute("src") || "", ...((img.getAttribute("srcset") || "").split(",").map((s: string) => s.trim().split(/\s+/)[0] || ""))];
    if (!urls.some((u) => u && photoKey(u) === key)) continue;
    // its caption: in its figure, or nearby in the small block that holds the photo (The Verge
    // nests the photo several levels inside and puts the caption beside that)
    let text = "";
    for (let up = img.parentElement, i = 0; up && i < 7 && up.tagName !== "BODY"; up = up.parentElement, i++) {
      const cap = up.querySelector("figcaption") || up.querySelector("[class*='caption' i]");
      if (cap) { text = squash(cap.textContent || ""); break; }
      if (squash(up.textContent || "").length > 300) break; // the article itself, not the photo's block
    }
    const fig = doc.createElement("figure");
    const pic = doc.createElement("img");
    pic.setAttribute("src", /^https?:/.test(img.getAttribute("src") || "") ? img.getAttribute("src") : og);
    pic.setAttribute("alt", img.getAttribute("alt") || "");
    fig.append(pic);
    if (text) { const fc = doc.createElement("figcaption"); fc.textContent = text; fig.append(fc); }
    return fig.outerHTML;
  }
  return "";
}

/** The article with its subtitle and lead photo first, unless it already begins with the one or shows the other. */
export function withLead(content: string, lead: string): string {
  if (!lead) return content;
  const dek = /^<p data-dek="">[\s\S]*?<\/p>/.exec(lead)?.[0] ?? "";
  const photo = lead.slice(dek.length);
  const src = /src="([^"]+)"/.exec(photo)?.[1] || "";
  const key = photoKey(src.replace(/&amp;/g, "&"));
  const hasPhoto = !photo || Array.from(content.matchAll(/<img\b[^>]*?\ssrc="([^"]+)"/gi)).some((m) => photoKey(m[1]!.replace(/&amp;/g, "&")) === key);
  const opening = squash(content.replace(/<[^>]+>/g, " ")).slice(0, 400).toLowerCase();
  const dekText = squash(dek.replace(/<[^>]+>/g, " ")).toLowerCase().slice(0, 60);
  const hasDek = !dek || opening.includes(dekText);
  // the article already opens with the subtitle (its header sits inside it): that paragraph is marked as the subtitle
  if (dek && hasDek) {
    const whole = squash(dek.replace(/<[^>]+>/g, " ")).toLowerCase();
    content = content.replace(/<p>([\s\S]*?)<\/p>/, (m, inner) => squash(inner.replace(/<[^>]+>/g, " ")).toLowerCase() === whole ? `<p data-dek="">${inner}</p>` : m);
  }
  return (hasDek ? "" : dek) + (hasPhoto ? "" : photo) + content;
}

export function focusArticleBody(doc: any): boolean {
  const marked = Array.from(doc.querySelectorAll('[itemprop="articleBody"]')) as any[];
  if (marked.length !== 1) return false;
  const body = marked[0];
  const text = (body.textContent || "").replace(/\s+/g, " ").trim();
  if (text.length < 20 && !body.querySelector("img, iframe, video, figure")) return false;
  const holder = doc.body;
  while (holder.firstChild) holder.removeChild(holder.firstChild);
  holder.appendChild(body);
  return true;
}

/**
 * Lists of other posts (Boing Boing's "next posts" under every article, "related",
 * "more stories") and author bio boxes go before Readability: their photos and teasers
 * would otherwise be read as the end of the article, and on a short post a bio can
 * outweigh the article itself (NSMB's video posts read as the writer's bike sizing).
 * Bylines go too (The Verge's carries the writer's photo); the reader shows the author.
 */
export function dropRelatedLists(doc: any): number {
  let dropped = 0;
  const related = /(^|[\s_-])((next|related|more|recommended|popular|trending|other|similar)[-_]?(posts?|articles?|stories|reads?|news|content)([-_]?(list|container|wrap|section|grid|block))?|read[-_]?next|you[-_]?may[-_]?also[-_]?like)([\s_-]|$)/i;
  const bio = /(^|[\s_-])((authors?|contributors?|writers?)[-_]?(bios?|box(es)?|info|cards?|about|profiles?)|byline)([\s_-]|$)/i;
  // a post's own footer (Pinkbike's #blog-foot: "Must Read This Week", a deals widget)
  const foot = /(^|[\s_-])(blog|post|article|entry|story)[-_]?foot(er)?([\s_-]|$)/i;
  for (const el of Array.from(doc.querySelectorAll("[id], [class]")) as any[]) {
    if (!el.isConnected || el.tagName === "BODY" || el.tagName === "HTML" || el.tagName === "ARTICLE" || el.tagName === "MAIN") continue;
    const names = `${el.getAttribute("id") || ""} ${el.getAttribute("class") || ""}`;
    if (!related.test(names) && !bio.test(names) && !foot.test(names)) continue;
    // never the article itself: a list of other posts has little prose of its own
    if (el.querySelectorAll("p").length > 12) continue;
    el.remove(); dropped++;
  }
  // story cards dropped into the article ("Vibe coding is turning…", The Verge): a photo whose
  // words are the headline of a link to another story, in a small block of its own
  for (const img of Array.from(doc.querySelectorAll("img[alt]")) as any[]) {
    const alt = squash(img.getAttribute("alt") || "").toLowerCase();
    if (!img.isConnected || alt.length < 20) continue;
    for (let up = img.parentElement, i = 0; up && i < 7 && up.tagName !== "BODY" && up.tagName !== "ARTICLE" && up.tagName !== "MAIN"; up = up.parentElement, i++) {
      if (squash(up.textContent || "").length > 400) break;
      const link = (Array.from(up.querySelectorAll("a[href]")) as any[]).find((a) => squash(a.textContent || "").toLowerCase() === alt);
      if (link) { up.remove(); dropped++; break; }
    }
  }
  // avatars (Gravatar serves the writer's picture on most WordPress sites)
  for (const img of Array.from(doc.querySelectorAll('img[src*="gravatar.com/avatar"]')) as any[]) { img.remove(); dropped++; }
  return dropped;
}

/**
 * Readers' comment threads go before Readability looks at the page: a long comment
 * must never be taken for the article (a Pinkbike video post once read as a reader's
 * pasted weather alert).
 */
export function dropCommentThreads(doc: any): number {
  let dropped = 0;
  const comment = /(^|[\s_-])(comments?|comment[-_]?(list|wrap|section|thread|area|container)|commentslist|disqus_thread)([\s_-]|$)/i;
  for (const el of Array.from(doc.querySelectorAll("[id], [class]")) as any[]) {
    if (!el.isConnected || el.tagName === "BODY" || el.tagName === "HTML" || el.tagName === "ARTICLE" || el.tagName === "MAIN") continue;
    if (comment.test(`${el.getAttribute("id") || ""} ${el.getAttribute("class") || ""}`)) { el.remove(); dropped++; }
  }
  return dropped;
}
